/**
 * Pure helpers that classify a drag-and-drop release into a concrete
 * operation (move, copy, no-op, invalid) and build move intents from
 * drag payload data alone.
 */

import type { DraggableData } from "./ids";
import type {
  FavoriteDropItem,
  FavoritesResourceType,
  TransferClassification,
  TransferDestination,
} from "./types";

/** Every destination the per-kind classifiers operate on directly. */
type FileSystemDropDestination = Extract<
  TransferDestination,
  { kind: "workspace" } | { kind: "canvas" } | { kind: "canvas_section" }
>;

type FlowSessionDropDestination = Extract<
  TransferDestination,
  { kind: "flow_session" }
>;

/**
 * Decide what should happen when a drag is released over `destination`.
 * Only depends on the payload data attached to the drag; does not
 * consult any cache.
 *
 * Core rule (D-XWS / D-XCLASS): same class → move, across class → copy.
 * Workspace-class files dropped on any workspace (including cross-workspace)
 * classify as move; non-workspace sources copy.
 */
export function classifyTransfer(
  drag: DraggableData,
  destination: TransferDestination,
): TransferClassification {
  if (destination.kind === "favorites") {
    return classifyFavoritesDrop(drag, destination.resourceType);
  }

  if (destination.kind === "flow_session") {
    return classifyFlowSessionDrop(drag, destination);
  }

  // Home/workspace composer — a chat that may not exist yet, so there is no
  // same-session no-op to detect; files copy, everything else is invalid
  // (mirrors the flow-session rules).
  if (destination.kind === "composer") {
    if (drag.kind === "files") {
      if (drag.payloads.length === 0) {
        return { kind: "noop", reason: "no payload" };
      }
      return { kind: "copy" };
    }
    return { kind: "invalid", reason: "Can't attach this to chat" };
  }

  // Filing a canvas into a folder is a MOVE (the actual re-file is done by a
  // surface-specific dispatch block via setCanvasFolder; this classification
  // only drives the drag pill's label).
  if (destination.kind === "canvas_folder") {
    if (drag.kind === "canvas-card" && drag.canvasIds.length > 0) {
      return {
        kind: "move_canvas",
        canvasId: drag.canvasIds[0],
        workspaceId: destination.workspaceId,
      };
    }
    return { kind: "invalid", reason: "Only canvases can be filed here" };
  }

  // Filing a chat into a folder is a MOVE (re-file via setFlowSessionFolder in
  // the dispatcher; this only drives the pill). Chats never change workspace by
  // filing, so both a cross-workspace drag and one with UNKNOWN provenance are
  // rejected — the dispatcher fails closed on the same condition, and the pill
  // must not promise a move the drop won't perform.
  if (destination.kind === "chat_folder") {
    if (drag.kind !== "chat-session") {
      return { kind: "invalid", reason: "Only chats can be filed here" };
    }
    if (drag.payload.sourceWorkspaceId !== destination.workspaceId) {
      return { kind: "invalid", reason: "Can't move chats between workspaces" };
    }
    // Always classify as a move so the chat folder/section shows a live "Move"
    // indicator even when the chat already lives there. A redundant drop is a
    // silent no-op — the dispatcher skips the write when nothing changes.
    return {
      kind: "move_chat",
      flowSessionId: drag.payload.flowSessionId,
      workspaceId: destination.workspaceId,
      folderId: destination.folderId,
    };
  }

  if (drag.kind === "canvas-card") {
    return classifyCanvasCard(drag, destination);
  }
  if (drag.kind === "section") {
    return classifySection(drag, destination);
  }
  if (drag.kind === "canvas-node") {
    return classifyCanvasNode(drag, destination);
  }
  if (drag.kind === "chat-session") {
    return classifyChatSession(drag, destination);
  }
  if (drag.kind === "integration-connection") {
    return { kind: "invalid", reason: "Connectors can't be dropped here" };
  }
  if (drag.kind === "skill") {
    return { kind: "invalid", reason: "Drag to the canvas board to place" };
  }
  if (drag.kind === "entity") {
    return { kind: "invalid", reason: "Drag to the canvas board to place" };
  }
  return classifyFiles(drag, destination);
}

/** @deprecated Use `classifyTransfer`. */
export const classifyExplorerDrop = classifyTransfer;

function classifyFlowSessionDrop(
  drag: DraggableData,
  destination: FlowSessionDropDestination,
): TransferClassification {
  if (drag.kind === "files") {
    return classifyFilesToFlowSession(drag, destination);
  }
  if (drag.kind === "canvas-card") {
    return { kind: "invalid", reason: "Can't move canvases into chat" };
  }
  if (drag.kind === "entity") {
    return { kind: "invalid", reason: "Drag to the canvas board to place" };
  }
  if (drag.kind === "chat-session") {
    if (drag.payload.flowSessionId === destination.flowSessionId) {
      return { kind: "noop", reason: "already in session" };
    }
    return { kind: "invalid", reason: "Can't move chats between sessions" };
  }
  return { kind: "invalid", reason: "Can't attach this to chat" };
}

function classifyFilesToFlowSession(
  drag: Extract<DraggableData, { kind: "files" }>,
  destination: FlowSessionDropDestination,
): TransferClassification {
  const { payloads } = drag;
  if (payloads.length === 0) {
    return { kind: "noop", reason: "no payload" };
  }

  const allFromSameSession = payloads.every(
    (p) =>
      p.sourceClass === "flow-session" &&
      p.sourceFlowSessionId === destination.flowSessionId,
  );
  if (allFromSameSession) {
    return { kind: "noop", reason: "already in session" };
  }

  return { kind: "copy" };
}

function classifyCanvasCard(
  drag: Extract<DraggableData, { kind: "canvas-card" }>,
  destination: FileSystemDropDestination,
): TransferClassification {
  if (destination.kind === "workspace") {
    // A files-only organizing surface (the rail Files section/folders) doesn't
    // hold canvases — file it in the Canvases section instead.
    if (destination.filesOnly) {
      return { kind: "invalid", reason: "Canvases go in the Canvases section" };
    }
    if (destination.parentPath !== "") {
      return { kind: "invalid", reason: "Can't move canvases into folders" };
    }
    if (drag.sourceWorkspaceId === destination.workspaceId) {
      // Already in this workspace — but a root drop still un-folders anything
      // filed in a folder (a move; the dispatcher does the op). Unknown
      // provenance (`undefined`, multi-canvas drags) counts as movable — only
      // a known-root drag (`null`) is a true no-op.
      if (drag.sourceFolderId !== null) {
        return {
          kind: "move_canvas",
          canvasId: drag.canvasIds[0],
          workspaceId: destination.workspaceId,
        };
      }
      return { kind: "noop", reason: "already in workspace" };
    }
    if (drag.canvasIds.length === 0) {
      return { kind: "noop", reason: "no canvas" };
    }
    return {
      kind: "move_canvas",
      canvasId: drag.canvasIds[0],
      workspaceId: destination.workspaceId,
    };
  }

  return { kind: "invalid", reason: "Can't move canvases here" };
}

function classifyChatSession(
  drag: Extract<DraggableData, { kind: "chat-session" }>,
  destination: FileSystemDropDestination,
): TransferClassification {
  if (destination.kind === "workspace") {
    // A files-only organizing surface (the rail Files section/folders) doesn't
    // hold chats — file it in the Chats section instead.
    if (destination.filesOnly) {
      return { kind: "invalid", reason: "Chats go in the Chats section" };
    }
    // Dropping a foldered chat on the workspace root un-folders it (move to
    // the root); a chat already at the root is a no-op — the detail page wraps
    // its whole surface in a root drop target, so an idle drag over the page
    // background must not read as an error (mirrors the canvas-card rule).
    // Chat folders only exist at the workspace level, so folder paths and
    // other workspaces are rejected.
    const { sourceWorkspaceId, sourceFolderId, flowSessionId } = drag.payload;
    if (
      destination.parentPath === "" &&
      (!sourceWorkspaceId || sourceWorkspaceId === destination.workspaceId)
    ) {
      if (!sourceFolderId) {
        return { kind: "noop", reason: "already at the workspace root" };
      }
      return {
        kind: "move_chat",
        flowSessionId,
        workspaceId: destination.workspaceId,
        folderId: null,
      };
    }
    return { kind: "invalid", reason: "Can't move chats into workspace" };
  }
  const { sourceCanvasId, flowSessionId, name } = drag.payload;
  if (destination.canvasId !== sourceCanvasId) {
    return { kind: "invalid", reason: "Can't move chats between canvases" };
  }
  return {
    kind: "place_chat",
    canvasId: destination.canvasId,
    flowSessionId,
    name,
    parentSectionId:
      destination.kind === "canvas_section" ? destination.sectionId : null,
  };
}

function classifySection(
  drag: Extract<DraggableData, { kind: "section" }>,
  destination: FileSystemDropDestination,
): TransferClassification {
  const { sourceCanvasId, files } = drag.payload;

  if (destination.kind === "workspace") {
    if (files.length === 0) {
      return { kind: "noop", reason: "section has no files" };
    }
    return {
      kind: "section_to_folder",
      workspaceId: destination.workspaceId,
      parentPath: destination.parentPath,
    };
  }

  if (destination.kind === "canvas_section") {
    return { kind: "invalid", reason: "Sections can't be nested" };
  }

  if (destination.canvasId !== sourceCanvasId) {
    return {
      kind: "invalid",
      reason: "Can't move sections between canvases",
    };
  }
  return { kind: "noop", reason: "already at root" };
}

function classifyCanvasNode(
  drag: Extract<DraggableData, { kind: "canvas-node" }>,
  destination: FileSystemDropDestination,
): TransferClassification {
  if (destination.kind === "workspace") {
    return { kind: "invalid", reason: "Can't move nodes into workspace" };
  }

  const { nodeId, sourceCanvasId, currentParentSectionId } = drag.payload;
  if (destination.canvasId !== sourceCanvasId) {
    return {
      kind: "invalid",
      reason: "Can't move nodes between canvases",
    };
  }

  if (destination.kind === "canvas") {
    if (currentParentSectionId === null) {
      return { kind: "noop", reason: "already at root" };
    }
    return {
      kind: "reparent_node",
      canvasId: destination.canvasId,
      nodeIds: [nodeId],
      targetParentSectionId: null,
    };
  }

  if (destination.sectionId === currentParentSectionId) {
    return { kind: "noop", reason: "already in this section" };
  }
  return {
    kind: "reparent_node",
    canvasId: destination.canvasId,
    nodeIds: [nodeId],
    targetParentSectionId: destination.sectionId,
  };
}

function classifyFiles(
  drag: Extract<DraggableData, { kind: "files" }>,
  destination: FileSystemDropDestination,
): TransferClassification {
  const { payloads } = drag;
  if (payloads.length === 0) {
    return { kind: "noop", reason: "no payload" };
  }

  if (destination.kind === "workspace") {
    const allWorkspaceClass = payloads.every(
      (p) => p.sourceClass === "workspace",
    );
    if (allWorkspaceClass) {
      return {
        kind: "move",
        workspaceId: destination.workspaceId,
        parentPath: destination.parentPath,
      };
    }
    return { kind: "copy" };
  }

  const destCanvasId = destination.canvasId;
  const allFromSameCanvas = payloads.every(
    (p) => p.sourceClass === "canvas" && p.sourceCanvasId === destCanvasId,
  );

  if (!allFromSameCanvas) {
    return { kind: "copy" };
  }

  const allPlaced = payloads.every((p) => p.sourcePlaced !== false);
  const targetSectionId =
    destination.kind === "canvas_section" ? destination.sectionId : null;

  if (!allPlaced) {
    return {
      kind: "place",
      canvasId: destCanvasId,
      parentSectionId: targetSectionId,
    };
  }

  const haveParentInfo = payloads.every(
    (p) => p.sourceParentSectionId !== undefined,
  );

  if (haveParentInfo) {
    const allAlreadyInTarget = payloads.every(
      (p) => (p.sourceParentSectionId ?? null) === targetSectionId,
    );
    if (allAlreadyInTarget) {
      return {
        kind: "noop",
        reason:
          targetSectionId === null
            ? "already at root"
            : "already in this section",
      };
    }
    const movingIds = payloads
      .filter((p) => (p.sourceParentSectionId ?? null) !== targetSectionId)
      .map((p) => p.recordId);
    return {
      kind: "reparent_node",
      canvasId: destCanvasId,
      nodeIds: movingIds,
      targetParentSectionId: targetSectionId,
    };
  }

  if (destination.kind === "canvas") {
    return { kind: "noop", reason: "same canvas" };
  }
  return {
    kind: "reparent_node",
    canvasId: destCanvasId,
    nodeIds: payloads.map((p) => p.recordId),
    targetParentSectionId: targetSectionId,
  };
}

function classifyFavoritesDrop(
  drag: DraggableData,
  resourceType: FavoritesResourceType,
): TransferClassification {
  const items = collectFavoriteDropItems(drag, resourceType);
  if (items === null) {
    return favoriteInvalidReason(resourceType);
  }
  return items.length > 0
    ? { kind: "favorite", items }
    : { kind: "noop", reason: "nothing to favorite" };
}

function collectFavoriteDropItems(
  drag: DraggableData,
  resourceType: FavoritesResourceType,
): FavoriteDropItem[] | null {
  switch (resourceType) {
    case "file-record": {
      if (drag.kind !== "files") return null;
      return drag.payloads
        .filter((p) => p.sourceClass === "workspace" && !p.isFolder)
        .map((p) => ({
          via: "resource-tag" as const,
          resourceType: "file-record" as const,
          resourceId: p.recordId,
        }));
    }
    case "canvas": {
      if (drag.kind !== "canvas-card") return null;
      return drag.canvasIds.map((resourceId) => ({
        via: "resource-tag" as const,
        resourceType: "canvas" as const,
        resourceId,
      }));
    }
    case "skill": {
      if (drag.kind !== "skill") return null;
      return drag.skillIds.map((resourceId) => ({
        via: "resource-tag" as const,
        resourceType: "skill" as const,
        resourceId,
      }));
    }
    case "integration-connection": {
      if (drag.kind !== "integration-connection") return null;
      return drag.connectionIds.map((resourceId) => ({
        via: "resource-tag" as const,
        resourceType: "integration-connection" as const,
        resourceId,
      }));
    }
    case "chat-inbox": {
      if (drag.kind !== "chat-session") return null;
      const inboxItemId = drag.payload.inboxItemId;
      if (!inboxItemId) return null;
      return [{ via: "inbox-star" as const, inboxItemId }];
    }
  }
}

function favoriteInvalidReason(
  resourceType: FavoritesResourceType,
): TransferClassification {
  const reasonByType: Record<FavoritesResourceType, string> = {
    "file-record": "Only files can be favorited here",
    canvas: "Only canvases can be favorited here",
    skill: "Only skills can be favorited here",
    "integration-connection": "Only connectors can be favorited here",
    "chat-inbox": "Only chats can be favorited here",
  };
  return { kind: "invalid", reason: reasonByType[resourceType] };
}
